import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import axios from '../../interceptors/adminAxios';

interface Props {
  deviceId: string;
  onBack: () => void;
}

interface Playlist  { id: number; name: string; duration_ms: number; }
interface Schedule  { id: number; name: string; }
interface Command   { id: number; command: string; status: string; created_at: string; sent_at: string | null; executed_at: string | null; result: string | null; payload: any; }
interface LogEntry   { id: number; device_id: string; category: string; level: string; message: string; created_at: string; }
interface Heartbeat  { heartbeat_active_sec: number; heartbeat_passive_sec: number; heartbeat_active_window_min: number; }

interface DeviceDetail {
  id: number;
  device_id: string;
  display_name: string;
  company_id: string | null;
  company_display_name: string;
  company_color: string;
  online: boolean;
  last_seen_at: string | null;
  app_version: string | null;
  ip_address: string | null;
  schedule_id: number | null;
  default_playlist_id: number | null;
  default_media_id: number | null;
  default_content_type: string | null;
  screen_orientation: number;
  content_rotation: number;
  kiosk_mode: boolean;
  disable_standby: boolean;
  autostart_on_boot: boolean;
  debug_mode: boolean;
  silent_log: boolean;
  cache_ttl_days: number;
  offline_timeout_days: number;
  storage_free_bytes: number | null;
  storage_total_bytes: number | null;
  config_version: number;
  files_on_device: number[] | null;
  playlists: Playlist[];
  schedules: Schedule[];
  commands: Command[];
}

type TabId = 'settings' | 'control' | 'commands' | 'logs' | 'files';

const ONLINE_MS = 2 * 60 * 1000;
const isOnline = (last: string | null) => !!last && Date.now() - new Date(last).getTime() < ONLINE_MS;

const fmtRelative = (d: string | null) => {
  if (!d) return 'never';
  const s = Math.round((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const fmtDateTime = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fmtBytes = (b: number | null) => {
  if (b === null) return '—';
  if (b >= 1e9) return `${(b / 1e9).toFixed(1)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(1)} MB`;
  return `${(b / 1e3).toFixed(0)} KB`;
};

const fmtDurationMs = (ms: number) => {
  const min = Math.floor(ms / 60000);
  const sec = Math.floor((ms % 60000) / 1000);
  return min > 0 ? `${min}m ${sec}s` : `${sec}s`;
};

const ORIENT_LABELS = ['Landscape', 'Portrait', 'Reverse Landscape', 'Reverse Portrait'];

const STATUS_COLOR: Record<string, string> = {
  pending: '#fbbf24', sent: '#60a5fa', done: '#4ade80', error: '#f87171',
};

const LEVEL_COLORS: Record<string, { color: string; bg: string }> = {
  info:    { color: '#60a5fa', bg: 'rgba(96,165,250,.12)'  },
  warning: { color: '#fbbf24', bg: 'rgba(251,191,36,.12)'  },
  error:   { color: '#f87171', bg: 'rgba(248,113,113,.12)' },
  debug:   { color: '#94a3b8', bg: 'rgba(148,163,184,.12)' },
};

const glassCard = { background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid var(--glass-border)', borderRadius: '14px', overflow: 'hidden' };

const inpStyle: React.CSSProperties = {
  background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 7,
  padding: '5px 9px', fontSize: 12, color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit', width: '100%',
};

const AdminDeviceDetailPage: React.FC<Props> = ({ deviceId, onBack }) => {
  const [tab, setTab]     = useState<TabId>('settings');
  const [device, setDevice] = useState<DeviceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [draft, setDraft]     = useState<Partial<DeviceDetail>>({});

  const [cmdInput, setCmdInput]     = useState('');
  const [cmdPayload, setCmdPayload] = useState('');
  const [cmdSending, setCmdSending] = useState(false);
  const [cmdMsg, setCmdMsg]         = useState('');

  const [updSending, setUpdSending] = useState(false);
  const [updMsg, setUpdMsg]         = useState('');
  const [latestVer, setLatestVer]   = useState<{ version_name: string; version_code: number } | null>(null);

  const [logs, setLogs]             = useState<LogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsLevel, setLogsLevel]   = useState('');
  const [logsCategory, setLogsCategory] = useState('');
  const [logsLoaded, setLogsLoaded]   = useState(false);
  const [heartbeat, setHeartbeat]     = useState<Heartbeat | null>(null);
  const [companies, setCompanies]     = useState<{ name: string; display_name: string }[]>([]);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params: Record<string, string> = { device_id: deviceId, limit: '200' };
      if (logsLevel)    params.level    = logsLevel;
      if (logsCategory) params.category = logsCategory;
      const res = await axios.get<LogEntry[]>('/admin/logs', { params });
      setLogs(res.data);
      setLogsLoaded(true);
    } finally { setLogsLoading(false); }
  }, [deviceId, logsLevel, logsCategory]);

  useEffect(() => {
    if (tab === 'logs') loadLogs();
  }, [tab, loadLogs]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, hbRes, coRes] = await Promise.all([
        axios.get<DeviceDetail>(`/admin/devices/${deviceId}`),
        axios.get<Heartbeat>('/admin/portal-settings/heartbeat'),
        axios.get<{ name: string; display_name: string }[]>('/admin/companies'),
      ]);
      setHeartbeat(hbRes.data);
      setDevice(res.data);
      // '_defaults_' is the portal-defaults holder, not a real company
      setCompanies((coRes.data || []).filter(c => c.name !== '_defaults_'));
      setDraft({
        company_id:        res.data.company_id,
        display_name:      res.data.display_name,
        schedule_id:       res.data.schedule_id,
        default_playlist_id: res.data.default_playlist_id,
        default_content_type: res.data.default_content_type,
        screen_orientation: res.data.screen_orientation,
        content_rotation:  res.data.content_rotation,
        kiosk_mode:        res.data.kiosk_mode,
        disable_standby:   res.data.disable_standby,
        autostart_on_boot: res.data.autostart_on_boot,
        debug_mode:        res.data.debug_mode,
        silent_log:        res.data.silent_log,
        cache_ttl_days:         res.data.cache_ttl_days,
        offline_timeout_days:   res.data.offline_timeout_days ?? 30,
      });
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.patch<DeviceDetail>(`/admin/devices/${deviceId}`, draft);
      setDevice(res.data);
      // a company change clears playlist/schedule server-side, so take them back
      setDraft(d => ({
        ...d,
        company_id:          res.data.company_id,
        default_playlist_id: res.data.default_playlist_id,
        schedule_id:         res.data.schedule_id,
      }));
    } finally {
      setSaving(false);
    }
  };

  const fetchLatestVer = async () => {
    try {
      const apiBase = process.env.REACT_APP_API_URL ?? 'http://localhost:3002/api';
      const staticBase = apiBase.replace(/\/api\/?$/, '');
      const r = await fetch(`${staticBase}/uploads/apk/version.json`);
      if (!r.ok) throw new Error('version.json not found');
      const ver = await r.json();
      setLatestVer(ver);
    } catch {
      setLatestVer(null);
    }
  };

  React.useEffect(() => { fetchLatestVer(); }, []);

  const handlePushUpdate = async () => {
    setUpdSending(true);
    setUpdMsg('');
    try {
      const apiBase = process.env.REACT_APP_API_URL ?? 'http://localhost:3002/api';
      const staticBase = apiBase.replace(/\/api\/?$/, '');
      let ver = latestVer;
      if (!ver) {
        const r = await fetch(`${staticBase}/uploads/apk/version.json`);
        if (!r.ok) throw new Error('Cannot fetch version.json');
        ver = await r.json();
        setLatestVer(ver);
      }
      const payload = {
        download_url: `${staticBase}/uploads/apk/promovideo.apk`,
        version_name: ver!.version_name,
        version_code: ver!.version_code,
      };
      await axios.post(`/admin/devices/${deviceId}/commands`, { command: 'update_app', payload });
      setUpdMsg(`Update v${ver!.version_name} queued — device will download on next heartbeat`);
      await load();
    } catch {
      setUpdMsg('Error sending update command');
    } finally {
      setUpdSending(false);
    }
  };

  const handleSendCmd = async () => {
    if (!cmdInput.trim()) return;
    setCmdSending(true);
    setCmdMsg('');
    try {
      let payload: any = undefined;
      if (cmdPayload.trim()) {
        try { payload = JSON.parse(cmdPayload); } catch { setCmdMsg('Invalid JSON payload'); setCmdSending(false); return; }
      }
      await axios.post(`/admin/devices/${deviceId}/commands`, { command: cmdInput.trim(), payload });
      setCmdMsg('Command sent');
      setCmdInput('');
      setCmdPayload('');
      await load();
    } catch {
      setCmdMsg('Error sending command');
    } finally {
      setCmdSending(false);
    }
  };

  // an unassigned device stores '' in one place and null in another
  const movingCompany = !!device && (draft.company_id || '') !== (device.company_id || '');

  const set = (k: keyof DeviceDetail, v: any) => setDraft(prev => ({ ...prev, [k]: v }));

  const setCompany = (name: string | null) => setDraft(prev => ({
    ...prev,
    company_id: name,
    // The server clears these on a move anyway; emptying them here keeps the form
    // honest instead of offering content the device is about to lose. Picking the
    // original company back puts the saved values in again.
    ...((name || '') !== (device?.company_id || '')
      ? { default_playlist_id: null, default_media_id: null, schedule_id: null }
      : {
          default_playlist_id: device?.default_playlist_id ?? null,
          default_media_id:    device?.default_media_id ?? null,
          schedule_id:         device?.schedule_id ?? null,
        }),
  }));

  const tabSx = (id: TabId) => ({
    px: '12px', py: '5px', borderRadius: '7px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    bgcolor: tab === id ? 'var(--selected-bg)' : 'transparent',
    border: `1px solid ${tab === id ? 'var(--selected-border)' : 'transparent'}`,
    color: tab === id ? 'var(--text-primary)' : 'var(--text-disabled)',
    userSelect: 'none',
  });

  const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '12px', alignItems: 'center', py: '7px', borderBottom: '1px solid var(--divider)' }}>
      <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</Typography>
      <Box>{children}</Box>
    </Box>
  );

  const Toggle: React.FC<{ value: boolean; onChange: (v: boolean) => void }> = ({ value, onChange }) => (
    <Box onClick={() => onChange(!value)}
      sx={{ width: 36, height: 20, borderRadius: 10, background: value ? 'var(--accent)' : 'var(--glass-border)', cursor: 'pointer', position: 'relative', transition: 'background .15s', flexShrink: 0 }}>
      <Box sx={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: value ? 19 : 3, transition: 'left .15s' }} />
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ color: 'var(--text-disabled)', fontSize: 13 }}>Loading…</Typography>
      </Box>
    );
  }

  if (!device) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ color: '#f87171', fontSize: 13 }}>Device not found</Typography>
      </Box>
    );
  }

  const on = isOnline(device.last_seen_at);
  const selectedPlaylist = device.playlists.find(p => p.id === draft.default_playlist_id);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: '16px', gap: '12px' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        <Box component="button" onClick={onBack}
          sx={{ display: 'flex', alignItems: 'center', gap: '5px', px: '10px', py: '5px', borderRadius: '7px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', '&:hover': { color: 'var(--text-primary)', background: 'var(--glass-hover)' } }}>
          ← Devices
        </Box>
        <Box sx={{ width: 1, height: 20, background: 'var(--divider)' }} />
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: on ? '#4ade80' : '#94a3b8', boxShadow: on ? '0 0 6px #4ade80' : 'none' }} />
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{device.display_name}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', px: '8px', py: '3px', borderRadius: '6px', background: `${device.company_color}18`, border: `1px solid ${device.company_color}40` }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: device.company_color }} />
          <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)' }}>{device.company_display_name}</Typography>
        </Box>
        {device.app_version && (
          <Box sx={{ px: '8px', py: '3px', borderRadius: '6px', background: 'var(--glass-hover)', border: '1px solid var(--glass-border)' }}>
            <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>v{device.app_version}</Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: 11, color: on ? '#4ade80' : 'var(--text-disabled)', ml: 'auto' }}>
          {fmtRelative(device.last_seen_at)}
        </Typography>
        <Box component="button" onClick={load}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '7px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-secondary)', cursor: 'pointer', '&:hover': { color: 'var(--accent-light)', borderColor: 'var(--accent)' } }}>
          ↻
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
        {(['settings', 'control', 'commands', 'logs', 'files'] as TabId[]).map(t => (
          <Box key={t} sx={tabSx(t)} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === 'commands' && device.commands.length > 0 && (
              <Box component="span" sx={{ ml: '5px', fontSize: 10, fontWeight: 700, px: '4px', py: '1px', borderRadius: '8px', background: 'var(--accent-glow)', color: 'var(--accent-light)' }}>
                {device.commands.length}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* ── Settings ── */}
        {tab === 'settings' && (
          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', minHeight: 0 }}>

            {/* Storage & Stats */}
            <Box sx={{ display: 'flex', gap: '12px' }}>
              {/* Storage */}
              <Box sx={{ ...glassCard, flex: 1 }}>
                <Box sx={{ px: '16px', py: '10px', borderBottom: '1px solid var(--divider)' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Storage</Typography>
                </Box>
                <Box sx={{ px: '16px', py: '12px', display: 'flex', gap: '24px' }}>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '2px' }}>Free</Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#4ade80', fontFamily: '"JetBrains Mono", monospace' }}>{fmtBytes(device.storage_free_bytes)}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '2px' }}>Total</Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", monospace' }}>{fmtBytes(device.storage_total_bytes)}</Typography>
                  </Box>
                  {device.storage_total_bytes && device.storage_free_bytes !== null && (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                      <Box sx={{ height: 6, borderRadius: 3, background: 'var(--divider)', overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', borderRadius: 3, background: 'var(--accent)', width: `${(1 - device.storage_free_bytes / device.storage_total_bytes) * 100}%` }} />
                      </Box>
                      <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', mt: '4px' }}>
                        {Math.round((1 - device.storage_free_bytes / device.storage_total_bytes) * 100)}% used
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Content stats */}
              <Box sx={{ ...glassCard, flex: 1 }}>
                <Box sx={{ px: '16px', py: '10px', borderBottom: '1px solid var(--divider)' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Content</Typography>
                </Box>
                <Box sx={{ px: '16px', py: '12px', display: 'flex', gap: '24px' }}>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '2px' }}>Playlist duration</Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--accent-light)', fontFamily: '"JetBrains Mono", monospace' }}>
                      {selectedPlaylist ? fmtDurationMs(selectedPlaylist.duration_ms) : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '2px' }}>Config version</Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--text-secondary)', fontFamily: '"JetBrains Mono", monospace' }}>v{device.config_version}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '2px' }}>Device ID</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", monospace', letterSpacing: '.1em' }}>{device.device_id}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Heartbeat intervals */}
            {heartbeat && (
              <Box sx={{ ...glassCard }}>
                <Box sx={{ px: '16px', py: '10px', borderBottom: '1px solid var(--divider)' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Heartbeat intervals</Typography>
                </Box>
                <Box sx={{ px: '16px', py: '12px', display: 'flex', gap: '32px' }}>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '2px' }}>Active mode</Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#4ade80', fontFamily: '"JetBrains Mono", monospace' }}>{heartbeat.heartbeat_active_sec}<Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: 'var(--text-disabled)', ml: '4px' }}>s</Box></Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '2px' }}>Sleep mode</Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#60a5fa', fontFamily: '"JetBrains Mono", monospace' }}>{heartbeat.heartbeat_passive_sec}<Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: 'var(--text-disabled)', ml: '4px' }}>s</Box></Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '2px' }}>Active window</Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#fbbf24', fontFamily: '"JetBrains Mono", monospace' }}>{heartbeat.heartbeat_active_window_min}<Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: 'var(--text-disabled)', ml: '4px' }}>min</Box></Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', maxWidth: 260, lineHeight: 1.5 }}>
                      Device polls every <b style={{ color: 'var(--text-secondary)' }}>{heartbeat.heartbeat_active_sec}s</b> while active, every <b style={{ color: 'var(--text-secondary)' }}>{heartbeat.heartbeat_passive_sec}s</b> in sleep. Active window lasts <b style={{ color: 'var(--text-secondary)' }}>{heartbeat.heartbeat_active_window_min} min</b> after last interaction.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Editable settings */}
            <Box sx={{ ...glassCard }}>
              <Box sx={{ px: '16px', py: '10px', borderBottom: '1px solid var(--divider)' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Settings</Typography>
              </Box>
              <Box sx={{ px: '16px', py: '8px' }}>
                <Row label="Company">
                  <select style={inpStyle} value={draft.company_id ?? ''} onChange={e => setCompany(e.target.value || null)}>
                    <option value="">— none —</option>
                    {companies.map(c => (
                      <option key={c.name} value={c.name}>{c.display_name || c.name}</option>
                    ))}
                  </select>
                  {movingCompany && (
                    <Typography sx={{ fontSize: 11, color: '#d97706', mt: '4px' }}>
                      Playlist and schedule are cleared by the move
                      {device.company_id ? ` — they belong to ${device.company_display_name}` : ''}.
                      Pick new ones after saving.
                    </Typography>
                  )}
                </Row>
                <Row label="Display name">
                  <input style={inpStyle} value={draft.display_name ?? ''} onChange={e => set('display_name', e.target.value)} />
                </Row>
                <Row label="Default playlist">
                  <select style={inpStyle} value={String(draft.default_playlist_id ?? '')} disabled={movingCompany} onChange={e => set('default_playlist_id', e.target.value ? Number(e.target.value) : null)}>
                    <option value="">— none —</option>
                    {!movingCompany && device.playlists.map(p => (
                      <option key={p.id} value={String(p.id)}>{p.name} ({fmtDurationMs(p.duration_ms)})</option>
                    ))}
                  </select>
                </Row>
                <Row label="Schedule">
                  <select style={inpStyle} value={String(draft.schedule_id ?? '')} disabled={movingCompany} onChange={e => set('schedule_id', e.target.value ? Number(e.target.value) : null)}>
                    <option value="">— none —</option>
                    {!movingCompany && device.schedules.map(s => (
                      <option key={s.id} value={String(s.id)}>{s.name}</option>
                    ))}
                  </select>
                </Row>
                <Row label="Default content type">
                  <select style={inpStyle} value={draft.default_content_type ?? ''} onChange={e => set('default_content_type', e.target.value || null)}>
                    <option value="">— none —</option>
                    <option value="playlist">Playlist</option>
                    <option value="media">Media</option>
                    <option value="off">Off</option>
                  </select>
                </Row>
                <Row label="Screen orientation">
                  <select style={inpStyle} value={String(draft.screen_orientation ?? 0)} onChange={e => set('screen_orientation', Number(e.target.value))}>
                    {ORIENT_LABELS.map((l, i) => <option key={i} value={String(i)}>{l}</option>)}
                  </select>
                </Row>
                <Row label="Content rotation">
                  <select style={inpStyle} value={String(draft.content_rotation ?? 0)} onChange={e => set('content_rotation', Number(e.target.value))}>
                    {['0°', '90°', '180°', '270°'].map((l, i) => <option key={i} value={String(i)}>{l}</option>)}
                  </select>
                </Row>
                <Row label="Cache TTL (days)">
                  <input style={{ ...inpStyle, width: 80 }} type="number" min={0} value={draft.cache_ttl_days ?? 0} onChange={e => set('cache_ttl_days', Number(e.target.value))} />
                </Row>
                <Row label="Offline timeout (days, 0=unlimited)">
                  <input style={{ ...inpStyle, width: 80 }} type="number" min={0} value={draft.offline_timeout_days ?? 30} onChange={e => set('offline_timeout_days', Number(e.target.value))} />
                </Row>
                <Row label="Kiosk mode">
                  <Toggle value={!!draft.kiosk_mode} onChange={v => set('kiosk_mode', v)} />
                </Row>
                <Row label="Disable standby">
                  <Toggle value={!!draft.disable_standby} onChange={v => set('disable_standby', v)} />
                </Row>
                <Row label="Autostart on boot">
                  <Toggle value={!!draft.autostart_on_boot} onChange={v => set('autostart_on_boot', v)} />
                </Row>
                <Row label="Debug mode (overlay + all logs)">
                  <Toggle value={!!draft.debug_mode} onChange={v => set('debug_mode', v)} />
                </Row>
                <Row label="Silent log (no overlay, all logs)">
                  <Toggle value={!!draft.silent_log} onChange={v => set('silent_log', v)} />
                </Row>
              </Box>
              <Box sx={{ px: '16px', py: '10px', borderTop: '1px solid var(--divider)', display: 'flex', gap: '8px' }}>
                <Box component="button" onClick={handleSave} disabled={saving}
                  sx={{ px: '16px', py: '6px', borderRadius: '7px', fontSize: 12, fontWeight: 600, border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </Box>
                <Box component="button" onClick={load}
                  sx={{ px: '12px', py: '6px', borderRadius: '7px', fontSize: 12, border: '1px solid var(--glass-border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  Reset
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Control ── */}
        {tab === 'control' && (
          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', minHeight: 0 }}>

            {/* App Update card */}
            <Box sx={glassCard}>
              <Box sx={{ px: '16px', py: '10px', borderBottom: '1px solid var(--divider)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.08em' }}>App Update</Typography>
                {latestVer && (
                  <Box sx={{ px: '8px', py: '2px', borderRadius: '6px', background: 'var(--glass-hover)', border: '1px solid var(--glass-border)' }}>
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>latest: v{latestVer.version_name}</Typography>
                  </Box>
                )}
                {device.app_version && latestVer && device.app_version === latestVer.version_name && (
                  <Box sx={{ px: '8px', py: '2px', borderRadius: '6px', background: 'rgba(74,222,128,.1)', border: '1px solid rgba(74,222,128,.3)' }}>
                    <Typography sx={{ fontSize: 11, color: '#4ade80' }}>up to date</Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ px: '16px', py: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                      Device: <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'var(--text-primary)' }}>v{device.app_version ?? '—'}</Box>
                      {latestVer && device.app_version !== latestVer.version_name && (
                        <Box component="span" sx={{ color: '#fbbf24', ml: '8px' }}>→ v{latestVer.version_name} available</Box>
                      )}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mt: '4px' }}>
                      Device will download the APK on next heartbeat and show the Install button in Settings.
                    </Typography>
                  </Box>
                  <Box component="button" onClick={handlePushUpdate} disabled={updSending}
                    sx={{ px: '16px', py: '8px', borderRadius: '8px', fontSize: 12, fontWeight: 700, border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer', flexShrink: 0, opacity: updSending ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                    {updSending ? 'Sending…' : 'Push Update'}
                  </Box>
                </Box>
                {updMsg && (
                  <Typography sx={{ fontSize: 12, color: updMsg.startsWith('Error') ? '#f87171' : '#4ade80' }}>{updMsg}</Typography>
                )}
              </Box>
            </Box>

            <Box sx={glassCard}>
              <Box sx={{ px: '16px', py: '10px', borderBottom: '1px solid var(--divider)' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Send Command</Typography>
              </Box>
              <Box sx={{ px: '16px', py: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input style={{ ...inpStyle, width: 200 }} placeholder="Command (e.g. screenshot)" value={cmdInput} onChange={e => setCmdInput(e.target.value)} />
                  <input style={{ ...inpStyle, flex: 1 }} placeholder='Payload JSON (optional, e.g. {"key":"value"})' value={cmdPayload} onChange={e => setCmdPayload(e.target.value)} />
                  <Box component="button" onClick={handleSendCmd} disabled={cmdSending || !cmdInput.trim()}
                    sx={{ px: '14px', py: '6px', borderRadius: '7px', fontSize: 12, fontWeight: 600, border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer', flexShrink: 0, opacity: (cmdSending || !cmdInput.trim()) ? 0.5 : 1 }}>
                    {cmdSending ? 'Sending…' : 'Send'}
                  </Box>
                </Box>
                {cmdMsg && (
                  <Typography sx={{ fontSize: 12, color: cmdMsg.startsWith('Error') ? '#f87171' : '#4ade80' }}>{cmdMsg}</Typography>
                )}
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>
                  Common commands: <code>screenshot</code>, <code>reload_config</code>, <code>clear_cache</code>, <code>get_logs</code>
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Commands ── */}
        {tab === 'commands' && (
          <Box sx={{ ...glassCard, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ px: '16px', py: '8px', borderBottom: '1px solid var(--divider)', display: 'grid', gridTemplateColumns: '80px 140px 80px 1fr 1fr 1fr', gap: '8px', background: 'var(--glass-hover)', flexShrink: 0 }}>
              {['ID', 'Command', 'Status', 'Created', 'Sent', 'Result'].map((h, i) => (
                <Typography key={i} sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)' }}>{h}</Typography>
              ))}
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {device.commands.length === 0 && (
                <Box sx={{ px: '16px', py: '20px' }}><Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>No commands yet</Typography></Box>
              )}
              {device.commands.map(cmd => (
                <Box key={cmd.id} sx={{ display: 'grid', gridTemplateColumns: '80px 140px 80px 1fr 1fr 1fr', gap: '8px', px: '16px', py: '8px', borderBottom: '1px solid var(--divider)', alignItems: 'start', '&:hover': { background: 'var(--row-hover)' } }}>
                  <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>#{cmd.id}</Typography>
                  <Typography sx={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>{cmd.command}</Typography>
                  <Box sx={{ display: 'inline-flex', width: 'fit-content' }}>
                    <Box sx={{ fontSize: 10, fontWeight: 700, px: '6px', py: '2px', borderRadius: '5px', background: `${STATUS_COLOR[cmd.status] ?? '#94a3b8'}18`, color: STATUS_COLOR[cmd.status] ?? '#94a3b8', border: `1px solid ${STATUS_COLOR[cmd.status] ?? '#94a3b8'}30` }}>{cmd.status}</Box>
                  </Box>
                  <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{fmtDateTime(cmd.created_at)}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{fmtDateTime(cmd.sent_at)}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{cmd.result ?? '—'}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── Logs ── */}
        {tab === 'logs' && (
          <Box sx={{ ...glassCard, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Filters */}
            <Box sx={{ px: '14px', py: '8px', borderBottom: '1px solid var(--divider)', display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
              <select value={logsLevel} onChange={e => setLogsLevel(e.target.value)} style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 7, padding: '4px 8px', fontSize: 12, color: 'var(--text-primary)', fontFamily: 'inherit', outline: 'none' }}>
                <option value="">All levels</option>
                {['info', 'warning', 'error', 'debug'].map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <select value={logsCategory} onChange={e => setLogsCategory(e.target.value)} style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 7, padding: '4px 8px', fontSize: 12, color: 'var(--text-primary)', fontFamily: 'inherit', outline: 'none' }}>
                <option value="">All categories</option>
                {['command', 'status', 'heartbeat', 'error', 'sync', 'auth'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <Box component="button" onClick={loadLogs} disabled={logsLoading}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '7px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, '&:hover': { color: 'var(--accent-light)', borderColor: 'var(--accent)' } }}>
                ↻
              </Box>
              <Typography sx={{ ml: 'auto', fontSize: 11, color: 'var(--text-disabled)' }}>
                {logsLoading ? 'Loading…' : `${logs.length} entries`}
              </Typography>
            </Box>
            {/* Header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '70px 80px 130px 1fr', gap: '8px', px: '14px', py: '6px', background: 'var(--glass-hover)', borderBottom: '1px solid var(--divider)', flexShrink: 0 }}>
              {['Level', 'Category', 'Time', 'Message'].map((h, i) => (
                <Typography key={i} sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)' }}>{h}</Typography>
              ))}
            </Box>
            {/* Rows */}
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {!logsLoaded && !logsLoading && (
                <Box sx={{ px: '14px', py: '16px' }}><Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>Select filters and press ↻</Typography></Box>
              )}
              {logsLoading && (
                <Box sx={{ px: '14px', py: '16px' }}><Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>Loading…</Typography></Box>
              )}
              {!logsLoading && logsLoaded && logs.length === 0 && (
                <Box sx={{ px: '14px', py: '16px' }}><Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>No logs found</Typography></Box>
              )}
              {!logsLoading && logs.map(log => {
                const lc = LEVEL_COLORS[log.level] ?? { color: '#94a3b8', bg: 'rgba(148,163,184,.1)' };
                return (
                  <Box key={log.id} sx={{ display: 'grid', gridTemplateColumns: '70px 80px 130px 1fr', gap: '8px', px: '14px', py: '6px', borderBottom: '1px solid var(--divider)', alignItems: 'start', '&:hover': { background: 'var(--row-hover)' } }}>
                    <Box sx={{ display: 'inline-flex' }}>
                      <Box sx={{ fontSize: 10, fontWeight: 700, px: '6px', py: '2px', borderRadius: '5px', background: lc.bg, color: lc.color, border: `1px solid ${lc.color}30`, whiteSpace: 'nowrap' }}>{log.level}</Box>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{log.category}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace', whiteSpace: 'nowrap' }}>
                      {new Date(log.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-primary)', wordBreak: 'break-word', lineHeight: 1.4 }}>{log.message}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ── Files ── */}
        {tab === 'files' && (
          <Box sx={{ ...glassCard, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ px: '16px', py: '10px', borderBottom: '1px solid var(--divider)', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
                Files on device
                {device.files_on_device && <Box component="span" sx={{ ml: '8px', fontSize: 11, fontWeight: 400, color: 'var(--text-disabled)' }}>{device.files_on_device.length} file(s)</Box>}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', px: '16px', py: '12px', minHeight: 0 }}>
              {(!device.files_on_device || device.files_on_device.length === 0) ? (
                <Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>No files reported by device</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {(device.files_on_device as number[]).map(fileId => (
                    <Box key={fileId} sx={{ px: '10px', py: '4px', borderRadius: '6px', background: 'var(--glass-hover)', border: '1px solid var(--glass-border)' }}>
                      <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: '"JetBrains Mono", monospace' }}>#{fileId}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}

      </Box>
    </Box>
  );
};

export default AdminDeviceDetailPage;
